
import { Component, Inject, Prop, Ref, Watch } from "vue-property-decorator";
import VueI18n from "vue-i18n";
import { TypedVue } from "@/store/types";
import adminService from "@/services/adminService";

const ASSISTANT_URL = "/assistant-chat.html";

// Matomo event category for all LDP Agent interactions. Each event carries the viewer's Auth0 id
// as the event name and the assessment id in an action-scoped custom dimension (1–3 are the
// visit-scoped dimensions set in router/index.ts).
const MATOMO_CATEGORY = "LDP Agent";
const MATOMO_ASSESSMENT_DIMENSION = "dimension4";

// After accepting we kick off a best-effort token refresh so the new acceptedAssistantToS claim
// lands as soon as Auth0 surfaces it. It runs fire-and-forget AFTER the chat is already open, so
// a slow/failed refresh-token grant can never block or hang the chat. Auth0's refresh-token grant
// does not always surface a freshly written app_metadata value into the ID token, so this module-
// level flag remembers acceptance for the rest of the SPA session — the gate is never re-shown if
// the component remounts (e.g. the user navigates away and back) before the next login, at which
// point the real claim is guaranteed present.
let assistantTosAcceptedThisSession = false;

@Component
export default class ElraReport8Assistant extends TypedVue {
  // Shadows the global $i18n with the per-report instance provided by CandidateReport.vue,
  // so $t() lookups here resolve against the report:elra v8 literals (matches ElraReport8.vue).
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ default: false }) visible!: boolean;
  @Prop({ required: true }) assessmentId!: string;

  @Ref("assistantFrame") private assistantFrame!: HTMLIFrameElement;

  hasMounted = false;
  readonly ASSISTANT_URL = ASSISTANT_URL;

  // ToS gate state: the chat iframe is only mounted once the user has accepted the
  // assistant-specific Terms of Service (persisted to the Auth0 user via acceptedAssistantTos).
  tosResolved = false;
  tosAccepted = false;
  tosLoading = false;

  // Gates the "Begin LDP" button: the user must tick the consent checkbox before accepting.
  consented = false;

  // The four "key terms" accordions. Titles/bodies are pulled from literals by key
  // (Report::v8::Assistant::ToS::Section::<key>::Title / ::Body); icon picks a static SVG below.
  readonly tosSections: ReadonlyArray<{ key: string; icon: string }> = [
    { key: "Internet", icon: "globe" },
    { key: "Anonymous", icon: "shield" },
    { key: "Session", icon: "clock" },
    { key: "Scope", icon: "info" },
  ];

  // Per-section expanded state (multi-open). Keys are pre-seeded so mutations stay reactive.
  openSections: Record<string, boolean> = { Internet: false, Anonymous: false, Session: false, Scope: false };

  // Static, inline decorative SVGs keyed by tosSections[].icon. Not content, so they live in code
  // rather than literals. Rendered via v-html into an HTML span (browsers parse inline <svg> fine).
  readonly TOS_ICONS: Record<string, string> = {
    globe:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
    shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
    clock:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
  };

  private directLineToken: string | null = null;
  private assistantReady = false;
  private messageHandler: ((e: MessageEvent) => void) | null = null;

  mounted() {
    this.messageHandler = (e: MessageEvent) => this.onMessage(e);
    window.addEventListener("message", this.messageHandler);
  }

  beforeDestroy() {
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
    }
  }

  @Watch("visible")
  async onVisibleChanged(open: boolean) {
    // Single choke point for open/close tracking, whatever triggered it (CTA, ×, Exit, Esc).
    this.track(open ? "Open" : "Close");

    if (!open || this.tosResolved) return;

    // Accepted earlier this session — skip the claim lookup (the token may not carry it yet).
    if (assistantTosAcceptedThisSession) {
      this.startAssistant();
      this.tosResolved = true;
      return;
    }

    try {
      const user = await this.$auth.getUser();
      if (user?.hasAcceptedAssistantTos()) {
        this.startAssistant();
      }
    } catch (e) {
      // If we can't determine acceptance, fall back to showing the ToS gate.
      console.error("Failed to resolve assistant ToS acceptance", e);
    } finally {
      this.tosResolved = true;
    }
  }

  private async startAssistant() {
    this.tosAccepted = true;
    if (!this.hasMounted) {
      this.hasMounted = true;
      await this.initAssistant();
    }
  }

  async onAccept() {
    this.tosLoading = true;
    try {
      await adminService.updateCurrentUser({ acceptedAssistantTos: true });
      assistantTosAcceptedThisSession = true;
      this.track("AcceptToS");
      // Await so the copilot-token fetch (its request interceptor calls getTokenSilently({}))
      // completes BEFORE the best-effort refresh below. auth0-spa-js dedupes concurrent
      // getTokenSilently calls by client::audience::scope (ignoring the ignoreCache flag), so an
      // overlapping ignoreCache refresh would otherwise hijack the interceptor's token read and,
      // if the refresh-token grant fails, the copilot request would never be sent.
      await this.startAssistant();
      // Best-effort, non-blocking: now that no token fetch is in flight, refresh so the new claim
      // lands as soon as Auth0 surfaces it. Failures are harmless (the session flag covers us).
      this.refreshTokenAfterAccept();
    } catch (e) {
      console.error("Failed to record assistant ToS acceptance", e);
    } finally {
      this.tosLoading = false;
    }
  }

  private async refreshTokenAfterAccept() {
    try {
      await this.$auth.getTokenSilently({ ignoreCache: true });
    } catch (e) {
      console.error("Best-effort token refresh after assistant ToS acceptance failed", e);
    }
  }

  toggleSection(key: string) {
    this.openSections[key] = !this.openSections[key];
  }

  onDeny() {
    this.track("DeclineToS");
    this.consented = false;
    this.close();
  }

  private async initAssistant() {
    try {
      const res = await adminService.getCopilotToken(this.assessmentId);
      this.directLineToken = res.token;
      this.trySendToken();
    } catch (e) {
      // Footer fallback ("Email a coach") remains visible; nothing else to do.
      console.error("Failed to load assistant token", e);
    }
  }

  private onMessage(e: MessageEvent) {
    if (e.origin !== window.location.origin) return;
    const data = e.data || {};
    if (data.type === "assistant-ready") {
      this.assistantReady = true;
      this.trySendToken();
    } else if (data.type === "assistant-message-sent") {
      this.track("SendMessage");
    }
  }

  private track(action: string) {
    this.$matomo && this.$matomo.trackEvent(MATOMO_CATEGORY, action, this.$auth.user?.sub, null, { [MATOMO_ASSESSMENT_DIMENSION]: this.assessmentId });
  }

  private trySendToken() {
    if (!this.assistantReady || !this.directLineToken) return;
    const frame = this.assistantFrame;
    if (frame && frame.contentWindow) {
      frame.contentWindow.postMessage({ type: "copilot-token", token: this.directLineToken }, window.location.origin);
    }
  }

  onVisibleChange(open: boolean) {
    this.$emit("update:visible", open);
  }

  close() {
    this.$emit("update:visible", false);
  }
}
